// Pickup management: shepherd attraction, drift, despawn on ttl, and
// collection by allies. Drops are handled in lifecycleSystem (where deaths
// happen) so the position is captured before the enemy is destroyed.
//
// Collection is reported via a callback so the scene can drive the
// breeding-panel modal without sim/render coupling.

import type { WorldState } from '../world-state';
import { C } from '../components';
import type { Position, FactionComp, Radius, PickupComp, EmergentComp, Stats } from '../components';
import { chance, type Rng } from '../rng';

/** Probability a slain enemy drops a breeding pickup. Tuned for ~1/30 kills. */
export const PICKUP_DROP_CHANCE = 0.035;

export function rollPickupDrop(rng: Rng): boolean {
  return chance(rng, PICKUP_DROP_CHANCE);
}

export interface PickupCallbacks {
  /** Fired when an ally collects a pickup; sim should pause and open UI. */
  onCollected: (x: number, y: number) => void;
}

/** Max distance the shepherd scan reaches when looking for shepherd allies. */
const SHEPHERD_SCAN_RADIUS = 300;

export function pickupsSystem(ws: WorldState, dt: number, cb: PickupCallbacks): void {
  const { world } = ws;
  const positions = world.store<Position>(C.position);
  const factions = world.store<FactionComp>(C.faction);
  const radii = world.store<Radius>(C.radius);
  const pickups = world.store<PickupComp>(C.pickup);
  const emergents = world.store<EmergentComp>(C.emergent);
  const stats = world.store<Stats>(C.stats);

  // Ttl tick + despawn + shepherd attraction. (Note: stats store is
  // referenced below in the collection pass; the explicit `void` quiets the
  // unused warning while letting TS keep the type-narrowed reference.)
  void stats;
  const toRemove: number[] = [];
  pickups.forEach((p, id) => {
    p.ttl -= dt;
    if (p.ttl <= 0) {
      toRemove.push(id);
      return;
    }
    const ppos = positions.get(id);
    if (!ppos) return;

    // Shepherd attraction: any nearby ally with a shepherd emergent pulls
    // this pickup toward it. Forces from multiple shepherds sum.
    let fx = 0;
    let fy = 0;
    ws.allyHash.forEachInRadius(ppos.x, ppos.y, SHEPHERD_SCAN_RADIUS, (aid) => {
      const shep = emergents.get(aid)?.effect.shepherd;
      if (!shep) return;
      const ap = positions.get(aid);
      if (!ap) return;
      const dx = ap.x - ppos.x;
      const dy = ap.y - ppos.y;
      const d = Math.hypot(dx, dy);
      if (d > shep.radius || d < 0.01) return;
      fx += (dx / d) * shep.force;
      fy += (dy / d) * shep.force;
    });
    if (fx !== 0 || fy !== 0) {
      ppos.x += fx * dt;
      ppos.y += fy * dt;
    }
  });

  // Collection: any ally touching a pickup collects it. The pickupAttraction
  // trait extends the effective collection radius per-ally so a magnetic
  // ball can grab pickups from a few pixels further away.
  const MAX_ATTRACT_BONUS = 20; // px at full pickupAttraction
  pickups.forEach((_p, pid) => {
    const ppos = positions.get(pid);
    const pr = radii.get(pid);
    if (!ppos || !pr) return;
    let collected = false;
    // Outer scan uses the largest possible collection radius so we don't
    // miss high-attraction allies.
    ws.allyHash.forEachInRadius(ppos.x, ppos.y, pr.r + MAX_ATTRACT_BONUS + 40, (aid) => {
      if (collected) return;
      if (factions.get(aid)?.side !== 'ally') return;
      const ap = positions.get(aid);
      const ar = radii.get(aid);
      if (!ap || !ar) return;
      const d = Math.hypot(ap.x - ppos.x, ap.y - ppos.y);
      const attract = stats.get(aid)?.sheet.pickupAttraction ?? 1;
      const reach = pr.r + ar.r + Math.max(0, attract - 1) * MAX_ATTRACT_BONUS;
      if (d <= reach) {
        collected = true;
        cb.onCollected(ppos.x, ppos.y);
        toRemove.push(pid);
      }
    });
  });

  for (const id of toRemove) world.destroyEntity(id);
}
